import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {createPortal} from 'react-dom';
import FocusTrap from 'focus-trap-react';
import cx from 'classnames';

import iconClose from '../../../assets/svg/icon-cross.svg';

const Modal = ({
  isOpen = false,
  size = 'md',
  triggerLabel,
  closeLabel = 'Close Modal',
  triggerClassName,
  children,
  container = document.body,
  toggleClass,
  toggleEvent,
  modalClassName,
}) => {
  const [open, setOpen] = useState(isOpen);
  const [isMounted, setIsMounted] = useState(true);

  const toggleScrollLock = () => document.querySelector('html')?.classList.toggle('scroll-lock');

  const toggleOpenClass = () => {
    const toggleClassString = toggleClass ? `${toggleClass}-` : '';
    document.querySelector('html')?.classList.toggle(`modal-${toggleClassString}open`);
  };

  const openModal = () => {
    setIsMounted(true);
    setOpen(true);
    toggleScrollLock();
    toggleOpenClass();
  };

  const closeModal = () => {
    setIsMounted(false);
    toggleScrollLock();
    toggleOpenClass();
  };

  const toggleModal = () => {
    if (!open) {
      const headerSection = document.querySelector('.header-section');
      if (document.body.classList.contains('has-header-bottom') && headerSection.offsetTop > 0) {
        window.scrollTo({
          top: headerSection.offsetTop + 1,
        });

        setTimeout(() => {
          openModal();
        }, 400);

        return;
      }

      openModal();
      return;
    }

    closeModal();
  };

  const handleAnimationEnd = () => {
    if (isMounted) return;
    setOpen(false);
  };

  const handleEscKey = (event) => event.keyCode === 27 && closeModal();

  const handleOutsideClick = (event) => event.target === event.currentTarget && closeModal();

  /**
   * The below useEffect() allows the modal to be toggled with a custom event; toggleEvent.
   */
  useEffect(() => {
    if (!toggleEvent) return;
    document.addEventListener(toggleEvent, toggleModal);
    return () => document.removeEventListener(toggleEvent, toggleModal);
  }, [open]);

  return (
    <>
      <button onClick={toggleModal} className={cx('modal__trigger', triggerClassName)} type="button">
        {triggerLabel}
      </button>

      {!open && null}

      {open &&
        createPortal(
          <FocusTrap
            focusTrapOptions={{
              allowOutsideClick: () => true,
              clickOutsideDeactivates: true,
              returnFocusOnDeactivate: true,
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
              className={cx(
                'modal',
                modalClassName,
                size === 'sm' && 'modal--sm',
                size === 'md' && 'modal--md',
                isMounted ? 'modal--mounted' : 'modal--unmounted'
              )}
              role="dialog"
              tabIndex={-1}
              aria-modal="true"
              onKeyDown={handleEscKey}
            >
              <div className="modal__dialog" onAnimationEnd={handleAnimationEnd}>
                <div className="modal__header">
                  <button
                    className="modal__close-button btn-none"
                    onClick={closeModal}
                    aria-label={closeLabel}
                    type="button"
                    dangerouslySetInnerHTML={{__html: iconClose}}
                  />
                </div>
                <div className="modal__body">{children}</div>
              </div>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
              <div className="modal__overlay" onClick={handleOutsideClick} />
            </div>
          </FocusTrap>,
          container
        )}
    </>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'full-screen']),
  triggerLabel: PropTypes.node,
  closeLabel: PropTypes.string,
  triggerClassName: PropTypes.string,
  children: PropTypes.node,
  container: PropTypes.node,
  toggleEvent: PropTypes.string,
  toggleClass: PropTypes.string,
  modalClassName: PropTypes.string,
};

export default Modal;
